import { Result, Spin } from 'antd';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '@/hooks/use-user/use-user';
import { syncSubscription } from '@/services/billing-service/billing.service';
import styles from './success.module.scss';

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export function Success() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { userData, refetch } = useUser();
  const attemptRef = useRef(0);
  const syncCalledRef = useRef(false);

  useEffect(() => {
    if (userData?.subscribed) {
      const timer = setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
      return () => clearTimeout(timer);
    }

    const activate = async () => {
      // Try syncing subscription directly from Stripe first
      if (!syncCalledRef.current) {
        syncCalledRef.current = true;
        try {
          await syncSubscription();
          const { data } = await refetch();
          if (data?.subscribed) {
            setTimeout(() => navigate('/', { replace: true }), 1500);
            return;
          }
        } catch {
          // Sync failed — fall through to polling (webhook may arrive)
        }
      }

      // Fall back to polling for webhook-based sync
      const interval = setInterval(async () => {
        attemptRef.current += 1;
        const { data } = await refetch();

        if (data?.subscribed || attemptRef.current >= MAX_POLL_ATTEMPTS) {
          clearInterval(interval);
          navigate('/', { replace: true });
        }
      }, POLL_INTERVAL_MS);

      return () => clearInterval(interval);
    };

    const cleanup = activate();
    return () => {
      cleanup?.then((fn) => fn?.());
    };
  }, [userData?.subscribed, navigate, refetch]);

  const isWaiting = !userData?.subscribed;

  return (
    <div className={styles.container}>
      <Result
        status="success"
        title={t('success.title')}
        subTitle={isWaiting ? t('success.activating') : t('success.active')}
        extra={isWaiting ? <Spin /> : undefined}
      />
    </div>
  );
}
